import { CartAddEvent, CartUpdateEvent } from '@theme/events';

// This element delegates cart rendering to the original theme's cart components.
// A shared lock prevents duplicate adds from rapid clicks or two cart surfaces.
class LtmGiftPackaging extends HTMLElement {
  static busy = false;

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = async (event) => {
    const button = event.target.closest('[data-gift-action]');
    if (!button || !this.contains(button) || button.disabled || LtmGiftPackaging.busy) return;
    event.preventDefault();
    LtmGiftPackaging.busy = true;
    const action = button.dataset.giftAction;
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;
    const root = window.Shopify?.routes?.root || '/';
    const previousFocusWasHere = this.contains(document.activeElement);
    const errorText = this.dataset.error;
    const disabledButtons = new Map();
    for (const control of document.querySelectorAll('ltm-gift-packaging button, button[name="checkout"]')) {
      disabledButtons.set(control, control.disabled);
      control.disabled = true;
    }
    for (const card of document.querySelectorAll('ltm-gift-packaging')) {
      card.setAttribute('aria-busy', 'true');
      const error = card.querySelector('.ltm-gift__error');
      if (error) error.hidden = true;
    }
    const blockCheckout = (e) => {
      const clicked = e.target.closest?.('button[name="checkout"], .additional-checkout-buttons, a[href*="/checkout"]');
      if (clicked || (e.type === 'submit' && e.submitter?.name === 'checkout')) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('click', blockCheckout, true);
    document.addEventListener('submit', blockCheckout, true);
    try {
      const readCart = async () => {
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error(errorText);
        return response.json();
      };
      const cartBefore = await readCart();
      const giftLines = cartBefore.items.filter((item) => String(item.product_id) === productId);
      const sections = [...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((component) => component.dataset.sectionId)
          .filter(Boolean)
      )].slice(0, 5);
      let result;
      if (action === 'add' && giftLines.length > 0) {
        // Another tab or an earlier click already added it; render rather than duplicate.
        const query = new URLSearchParams({ sections: sections.join(',') });
        const response = await fetch(`${location.pathname}?${query}`, { cache: 'no-store' });
        if (!response.ok) throw new Error(errorText);
        result = { sections: await response.json() };
      } else {
        const body = {
          sections,
          sections_url: location.pathname,
          ...(action === 'add'
            ? { items: [{ id: variantId, quantity: 1 }] }
            : { updates: Object.fromEntries(giftLines.map((item) => [item.key, 0])) }),
        };
        const response = await fetch(`${root}cart/${action === 'add' ? 'add' : 'update'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        result = await response.json();
        if (!response.ok || result.status >= 400 || result.errors) {
          throw new Error(typeof result.description === 'string' ? result.description : errorText);
        }
      }
      const cartAfter = await readCart();
      const data = {
        source: 'ltm-gift-packaging',
        itemCount: cartAfter.item_count,
        variantId,
        sections: result.sections,
      };
      const ThemeEvent = action === 'add' ? CartAddEvent : CartUpdateEvent;
      // Nova caches converted values in jQuery data on reused money nodes.
      // Clear only cart price caches before Horizon morphs those same nodes.
      // Otherwise removing a line can leave its converted price on another row.
      if (typeof window.bacurr$ === 'function') {
        for (const money of document.querySelectorAll('cart-items-component span.money')) {
          window.bacurr$(money).removeData();
          money.classList.remove('bacurr-money');
        }
      }
      document.dispatchEvent(new ThemeEvent(cartAfter, 'ltm-gift-packaging', data));
      if (typeof window.bacurr$ === 'function') {
        // Horizon's morph can finish asynchronously. Re-seed Nova from Shopify's
        // fresh section HTML, never from previously converted display text.
        for (let frame = 0; frame < 3; frame++) {
          await new Promise(requestAnimationFrame);
          let pending = false;
          for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
            const sectionId = component.dataset.sectionId;
            const html = result.sections?.[sectionId];
            if (!html) continue;
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const freshComponent = [...parsed.querySelectorAll('cart-items-component')]
              .find((node) => node.dataset.sectionId === sectionId);
            if (!freshComponent) continue;
            const rawMoney = [...freshComponent.querySelectorAll('span.money')];
            const liveMoney = [...component.querySelectorAll('span.money')];
            if (rawMoney.length !== liveMoney.length) {
              pending = true;
              continue;
            }
            liveMoney.forEach((money, index) => {
              window.bacurr$(money).removeData();
              money.classList.remove('bacurr-money');
              money.textContent = rawMoney[index].textContent;
            });
          }
          if (!pending) break;
        }
        window.baCurr?.refreshConversion?.();
      }
      if (action === 'add') {
        document.dispatchEvent(new CustomEvent('cart:add', {
          detail: { id: Number(variantId), qty: 1, source: 'ltm-gift-packaging' },
        }));
      }
      if (previousFocusWasHere) {
        requestAnimationFrame(() => {
          const cards = [...document.querySelectorAll('ltm-gift-packaging')];
          const visibleCard = cards.find((card) => card.getBoundingClientRect().height > 0);
          visibleCard?.querySelector('[data-gift-action]')?.focus({ preventScroll: true });
        });
      }
    } catch (error) {
      const cards = document.querySelectorAll('ltm-gift-packaging');
      for (const card of cards) {
        const message = card.querySelector('.ltm-gift__error');
        if (message) {
          message.textContent = error instanceof TypeError ? errorText : (error.message || errorText);
          message.hidden = false;
        }
      }
    } finally {
      LtmGiftPackaging.busy = false;
      document.removeEventListener('click', blockCheckout, true);
      document.removeEventListener('submit', blockCheckout, true);
      for (const [control, disabled] of disabledButtons) {
        if (control.isConnected) control.disabled = disabled;
      }
      for (const card of document.querySelectorAll('ltm-gift-packaging')) {
        card.setAttribute('aria-busy', 'false');
      }
    }
  };
}

if (!customElements.get('ltm-gift-packaging')) {
  customElements.define('ltm-gift-packaging', LtmGiftPackaging);
}
